import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

import { cx } from './cx';

type Variant = 'primary' | 'secondary' | 'danger' | 'ghost' | 'link';
type Size = 'md' | 'sm' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  icon?: ReactNode;
  iconRight?: ReactNode;
  loading?: boolean;
}

const variants: Record<Variant, string> = {
  primary: 'border-0 bg-btn text-btn-fg hover:bg-btn-h',
  secondary: 'border border-line2 bg-surface text-text hover:border-accent hover:text-accent',
  danger: 'border border-danger bg-transparent text-danger hover:bg-danger-bg',
  ghost: 'border-0 bg-transparent text-muted hover:bg-bg2 hover:text-text',
  link: 'border-0 bg-transparent p-0! min-h-0! text-accent hover:text-accent-h',
};

const sizes: Record<Size, string> = {
  sm: 'min-h-[38px] px-[14px] text-[13px]',
  md: 'min-h-10 px-4 text-sm',
  lg: 'min-h-12 px-4 text-[15px]',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    icon,
    iconRight,
    loading,
    className,
    children,
    disabled,
    type,
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cx(
        'inline-flex items-center justify-center gap-2 rounded-md font-semibold leading-none whitespace-nowrap transition-colors disabled:cursor-not-allowed disabled:opacity-55',
        variants[variant],
        sizes[size],
        className,
      )}
      {...rest}
    >
      {icon}
      {children}
      {iconRight}
    </button>
  );
});

export const IconButton = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & { label: string; active?: boolean; size?: number }
>(function IconButton(
  { label, active, size = 40, className, children, style, type, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      aria-label={label}
      title={label}
      style={{ width: size, height: size, ...style }}
      className={cx(
        'relative grid place-items-center rounded-md border transition-colors hover:border-accent hover:text-accent',
        active ? 'border-accent bg-chip text-accent' : 'border-line2 bg-transparent text-text',
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
});
